<template>
<v-flex xs12 lg9>
  <v-card class="py-4">
    <v-flex xs12 px-4 py-2 v-if="err">
      <v-alert :value="true" type="error">{{err}}</v-alert>
    </v-flex>
    <v-flex xs12 px-4 py-2 v-if="success">
      <v-alert :value="true" type="success">{{success}}</v-alert>
    </v-flex>
    <v-form v-model="valid">
      <v-layout row wrap justify-space-between align-center class="px-2">
        <v-flex xs12 sm6 px-3>
          <v-autocomplete
            v-if="!isLocalAdmin"
            :items="companies"
            :label="$lang.actions.selectCompany"
            v-model="printer.companyId"
            item-text="name"
            item-value="id"
            required
            :readonly="isEdit"
            :loading="companyLoading"
            :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
          />
        </v-flex>
        <v-flex xs12 sm6 px-3>
          <v-text-field
            :label="$lang.labels.printerName"
            v-model="printer.name"
            required
            :rules="[v => !!v || `${$lang.errors.missingPrinterName}`]"
          />
        </v-flex>
        <v-flex xs12 sm6 px-3>
          <v-text-field
            :label="$lang.header.manufacturer"
            v-model="printer.manufacturer"
            required
            :rules="[v => !!v || `${$lang.errors.missingManufacturer}`]"
          />
        </v-flex>
        <v-flex xs12 sm6 px-3>
          <v-text-field
            :label="$lang.header.model"
            v-model="printer.model"
            required
            :rules="[v => !!v || `${$lang.errors.missingModel}`]"
          />
        </v-flex>
        <v-flex xs12 sm6 px-3>
          <v-text-field
            :label="$lang.labels.ipAddress"
            v-model="printer.ipAddress"
            :disabled="pdfPrinter"
          />
        </v-flex>
        <v-flex xs12 sm6 px-3>
          <v-text-field
            :label="$lang.labels.portNumber"
            v-model="printer.portNumber"
            :disabled="pdfPrinter"
            :rules="[v => checkPort(v)]"
          />
        </v-flex>
        <v-flex xs12 sm6 px-3>
          <v-select
            :label="$lang.actions.selectPrinterCategories"
            multiple
            small-chips
            deletable-chips
            :items="sortedCategories"
            v-model="printer.printerCategories"
          />
        </v-flex>
        <v-flex xs12>
          <v-flex xs12 sm6 px-3>
            <v-textarea
              :label="$lang.labels.notes"
              v-model="printer.notes"
              auto-grow
              rows="1"
            />
          </v-flex>
        </v-flex>
        <v-flex xs12 sm6 px-3>
          <v-checkbox
            :label="$lang.labels.pdfPrinter"
            color="primary"
            class="option"
            hide-details
            v-model="pdfPrinter"
          />
        </v-flex>
      </v-layout>
      <v-layout row wrap justify-space-between class="mt-3 px-2">
        <v-text-field
          v-if="isEdit && createdOn"
          :label="$lang.labels.created"
          v-model="createdOn"
          readonly
          class="min-w max-w mx-3"
        />
        <v-text-field
          v-if="isEdit && modifiedOn"
          :label="$lang.labels.modified"
          v-model="modifiedOn"
          readonly
          class="min-w max-w mx-3"
        />
      </v-layout>
      <v-layout row wrap justify-space-between align-content-center class="px-2">
        <v-btn
          flat class="mx-3"
          @click="$router.push({ name: 'printers' })">
          <v-icon>keyboard_arrow_left</v-icon>
          {{$lang.actions.close}}
        </v-btn>
        <v-btn
          class="primary mx-3"
          :disabled="!valid"
          @click="submit">
          {{ isEdit ? $lang.actions.update : $lang.actions.create}}
        </v-btn>
      </v-layout>
    </v-form>
  </v-card>
</v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    data: () => ({
      err: '',
      success: '',
      valid: false,
      isEdit: false,
      companyLoading: true,
      pdfPrinter: false,
      companies: [],
      printerCategories: [],
      printer: {
        companyId: null,
        name: null,
        manufacturer: null,
        model: null,
        ipAddress: null,
        portNumber: null,
        printerCategories: [],
      },
      createdOn: '',
      modifiedOn: '',
      isLocalAdmin: false,
    }),
    watch: {
      pdfPrinter(val) {
        this.printer.manufacturer = val ? 'PDF' : '';
        this.printer.model = val ? 'PDF' : '';
        this.printer.controlledByMoon = val ? true : false;
      },
    },
    computed: {
      sortedCategories() {
        return this.printerCategories.sort();
      }
    },
    methods: {
      checkPort(val) {
        if (Number(val) < 0 || Number(val) > 65535) return 'Port outside valid range';
        return true;
      },
      formatDate(date) {
         return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      init() {
        const roles = JSON.parse(this.$cookie.get('roles') || '[]');
        this.isLocalAdmin = roles.includes('ROLE_SUN_LOCAL_ADMIN');
        return new Promise((resolve) => {
          this.$http.get('company')
            .then((res) => {
              resolve()
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                if (this.isLocalAdmin && this.companies.length === 1) this.printer.companyId = this.companies[0].id;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            });
          this.$http.get('system/fetchprintercategories')
            .then((res) => {
              resolve()
              if (res.body.status === 'Success') {
                this.printerCategories = res.body.printerCategories;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.err = err.body.statusText || err.body.message;
            });
        });
      },
      submit() {
        let url = this.isEdit ? `printer/update/${this.$route.params.id}` : 'printer/create';
        this.$http.post(url, this.printer)
          .then((res) => {
            if (res.body.status === 'Success') {
              this.success = this.isEdit ? 'Printer updated successfuly' : 'Printer created successfuly';
              setTimeout(() => {
                if (this.isEdit) {
                  this.success = '';
                } else {
                  this.$router.push({ name: 'printers' });
                }
              }, 3000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
    },
    created() {
      this.init()
        .then(() => {
          if (this.$route.params.id) {
            this.isEdit = true;
            this.$http.get(`printer/${this.$route.params.id}`)
              .then((res) => {
                if (res.body.status === 'Success') {
                  this.printer = res.body.printer;
                  this.createdOn = this.formatDate(this.printer.createdOn);
                  if (this.printer.modifiedOn) {
                    this.modifiedOn = this.formatDate(this.printer.modifiedOn);
                  }
                  if (this.printer.manufacturer === 'PDF' && this.printer.model === 'PDF') {
                    this.pdfPrinter = true;
                  }
                  return;
                }
                this.err = res.body.statusText
              }, (err) => {
                this.err = err.body.statusText || err.body.message;
              })
            }
        })
    },
  };
</script>




// WEBPACK FOOTER //
// PrinterDetails.vue?c3e008f6